import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CrewMerchantUi } from 'corso-types';
import api from '~/api';
import { useStoreId } from '~/providers/StoreUserProvider';

/**
 * A prefix for all tag-based queries for the purposes of easy invalidation.
 * @see https://tanstack.com/query/latest/docs/framework/react/guides/query-invalidation
 */
const queryKeyPrefix = 'registrationTags';

// * store registration tags

export function useStoreRegistrationTagsCreate() {
  const storeId = useStoreId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) =>
      api.store(storeId).storeRegistrationTags.create({ name }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [queryKeyPrefix, storeId] }),
  });
}

/** Available registration tags across the store. */
export function useStoreRegistrationTags<
  Data = CrewMerchantUi.RegistrationTag[],
>(select?: (tags: { id: number; name: string }[]) => Data) {
  const storeId = useStoreId();
  return useQuery({
    queryKey: [queryKeyPrefix, storeId],
    queryFn: () => api.store(storeId).storeRegistrationTags.list(),
    select,
  });
}

/** Delete a registration tag from the store. */
export function useStoreRegistrationTagDelete() {
  const storeId = useStoreId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tagId: number) =>
      api.store(storeId).storeRegistrationTags.delete(`${tagId}`),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [queryKeyPrefix, storeId] }),
  });
}

// * registration-scoped tags

/** Current tags for the registration. */
export function useRegistrationTags(registrationId: number) {
  const storeId = useStoreId();
  return useQuery({
    queryKey: [queryKeyPrefix, storeId, `${registrationId}`] as const,
    queryFn: ({ queryKey: [, , regId] }) =>
      api.store(storeId).registrations.tags({ registrationId: regId }).list(),
  });
}

/** Upsert tags for a specific registration. */
export function useRegistrationTagsUpsert(registrationId: number) {
  const storeId = useStoreId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tags: string[]) =>
      api
        .store(storeId)
        .registrations.tags({ registrationId: `${registrationId}` })
        .upsert(tags.map((name) => ({ name }))),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [queryKeyPrefix, storeId] }),
  });
}
